import { RouterProvider } from '@tanstack/react-router';

import { useAuthStore } from '@/features/auth/auth-store';
import { type DisplayRole } from '@/features/auth/roles';
import { router } from '@/router/router';

/**
 * Root of the BankIQ SPA (SCRUM-616). TanStack Router owns navigation, auth
 * guards, and history. Auth lives in the in-memory Zustand store (token-off-disk,
 * ADR-011 / SCRUM-102) and is INJECTED into the router context every render via
 * RouterProvider's `context` prop — the router is created once with a placeholder
 * context (router/router.tsx). Route `beforeLoad` guards read this live value:
 *   - /login redirects an authenticated user to their role landing,
 *   - the authed layout redirects an unauthenticated user to /login,
 *   - the index route redirects to the role landing (or the pending state).
 *
 * A hard refresh loses the in-memory session, so the guard sends the user to
 * /login — the deliberate token-off-disk behavior, now enforced by routing
 * rather than the old App.tsx store-gate + useHistoryGuard mitigation (removed).
 */
function InnerApp() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const role = (useAuthStore((s) => s.user?.role) ?? 'Bank Executive') as DisplayRole;
  return <RouterProvider router={router} context={{ auth: { isAuthenticated, role } }} />;
}

export default function App() {
  return <InnerApp />;
}
